(function (root) {
  // Accepts either a single image ({ src, alt }) or a set ({ items, index }).
  // Either way the result carries a non-empty `items` list when valid, and
  // `src`/`alt` mirror the starting image for callers that only read those.
  function normalizeLightboxDetail(detail) {
    var input = detail || {};
    var items = [];

    if (Array.isArray(input.items)) {
      input.items.forEach(function (item) {
        var itemSrc = String((item && item.src) || '').trim();
        if (itemSrc) {
          items.push({ src: itemSrc, alt: String((item && item.alt) || '') });
        }
      });
    }

    if (!items.length) {
      var singleSrc = String(input.src || '').trim();
      if (singleSrc) {
        items.push({ src: singleSrc, alt: String(input.alt || '') });
      }
    }

    var index = Math.max(0, Math.min(items.length - 1, Number(input.index) || 0));
    var current = items[index] || { src: '', alt: '' };

    return {
      src: current.src,
      alt: current.alt,
      items: items,
      index: index,
      trigger: input.trigger && typeof input.trigger.focus === 'function' ? input.trigger : null
    };
  }

  function dispatchImageLightbox(detail) {
    var normalized = normalizeLightboxDetail(detail);

    if (!normalized.src || !root.dispatchEvent || !root.CustomEvent) {
      return false;
    }

    root.dispatchEvent(new root.CustomEvent('image-lightbox:open', {
      detail: normalized
    }));

    return true;
  }

  function registerAlpine() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('imageLightbox', function () {
      return {
        open: false,
        src: '',
        alt: '',
        items: [],
        index: 0,
        zoomed: false,
        zoomWidth: '',
        previousTrigger: null,

        destroy() {
          root.themeModalAccessibility?.deactivate(this.$root);
        },

        openLightbox(event) {
          var detail = normalizeLightboxDetail(event && event.detail);

          if (!detail.src) {
            return;
          }

          this.items = detail.items;
          this.zoomed = false;
          this.zoomWidth = '';
          this.show(detail.index);
          this.previousTrigger = detail.trigger
            || (root.document.activeElement && typeof root.document.activeElement.focus === 'function'
              ? root.document.activeElement
              : null);
          this.open = true;
          root.themeModalAccessibility?.activate(this.$root);
          root.document.documentElement.classList.add('image-lightbox-open');
          this.$nextTick(function () {
            if (this.$refs.closeButton) {
              this.$refs.closeButton.focus();
            }
          }.bind(this));
        },

        closeLightbox() {
          if (!this.open) {
            return;
          }

          this.open = false;
          root.themeModalAccessibility?.deactivate(this.$root);
          root.document.documentElement.classList.remove('image-lightbox-open');
          root.dispatchEvent?.(new root.CustomEvent('image-lightbox:closed'));
          this.$nextTick(function () {
            if (this.previousTrigger) {
              this.previousTrigger.focus();
            }

            this.src = '';
            this.alt = '';
            this.items = [];
            this.index = 0;
            this.zoomed = false;
            this.zoomWidth = '';
          }.bind(this));
        },

        show(index) {
          var total = this.items.length;
          if (!total) {
            return;
          }

          this.index = ((Number(index) || 0) % total + total) % total;
          this.src = this.items[this.index].src;
          this.alt = this.items[this.index].alt;
          this.zoomed = false;
          this.zoomWidth = '';
        },

        showNext() {
          if (this.items.length > 1) {
            this.show(this.index + 1);
          }
        },

        showPrevious() {
          if (this.items.length > 1) {
            this.show(this.index - 1);
          }
        },

        // Fit-to-screen <-> full size. Full size is the image's real pixel
        // width, or twice the fitted width when the source is small, so the
        // control always does something. Zooming from a click keeps the
        // clicked spot in view.
        toggleZoom(event) {
          var image = this.$refs.image;
          var stage = this.$refs.stage;
          if (!image || !stage) {
            return;
          }

          if (this.zoomed) {
            this.zoomed = false;
            this.zoomWidth = '';
            return;
          }

          var rect = image.getBoundingClientRect();
          var focusX = 0.5;
          var focusY = 0.5;
          if (event && rect.width && rect.height && typeof event.clientX === 'number') {
            focusX = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
            focusY = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
          }

          var fittedWidth = rect.width || image.clientWidth || 0;
          var targetWidth = Math.max(image.naturalWidth || 0, Math.round(fittedWidth * 2));
          this.zoomWidth = targetWidth + 'px';
          this.zoomed = true;

          this.$nextTick(function () {
            stage.scrollLeft = Math.max(0, (stage.scrollWidth - stage.clientWidth) * focusX);
            stage.scrollTop = Math.max(0, (stage.scrollHeight - stage.clientHeight) * focusY);
          });
        },

        trapFocus(event) {
          var dialog = this.$root && this.$root.querySelector('.image-lightbox__dialog');
          if (root.themeModalAccessibility && typeof root.themeModalAccessibility.trapFocus === 'function') {
            root.themeModalAccessibility.trapFocus(event, dialog);
            return;
          }

          event.preventDefault();
          if (this.$refs.closeButton) {
            this.$refs.closeButton.focus();
          }
        },

        handleEscape(event) {
          if (!this.open) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeLightbox();
        }
      };
    });
  }

  root.imageLightbox = {
    open: dispatchImageLightbox
  };

  if (root.document) {
    root.document.addEventListener('alpine:init', registerAlpine);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      dispatchImageLightbox: dispatchImageLightbox,
      normalizeLightboxDetail: normalizeLightboxDetail
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
